import { useState, useEffect, useMemo } from "react";
import { useGlobalContext } from "../../context";
import api from "../../api";
import UserSideNav from "../components/UserSideNav";
import { Link } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { BiSolidUser, BiLogOut, BiSolidWallet, BiX, BiChevronLeft } from "react-icons/bi";
import { toast } from "sonner";
import userDashLinks from "./userDashLinks";
import { GiProfit } from "react-icons/gi";
import { HiCash } from "react-icons/hi";
import { FiTrendingUp } from "react-icons/fi";
import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";
import binance from "../../images/binance.png";
import trustwallet from "../../images/trustwallet.png";
import exoduswallet from "../../images/exodus.svg";
import gemini from "../../images/gemini.png";
import blockchain from "../../images/blockchain.png";
import coinbase from "../../images/coinbase.png";
import cryptodotcom from "../../images/crptodotcom.png";
import kukoin from "../../images/kuCoin.png";
import kraken from "../../images/kraken.png";

const WALLETS = [
  { name: "Binance", img: binance },
  { name: "Trust Wallet", img: trustwallet },
  { name: "Exodus", img: exoduswallet },
  { name: "Gemini", img: gemini },
  { name: "Blockchain", img: blockchain },
  { name: "Coinbase", img: coinbase },
  { name: "Crypto.com", img: cryptodotcom },
  { name: "KuCoin", img: kukoin },
  { name: "Kraken", img: kraken },
];

const statCards = (data) => [
  {
    label: "Balance",
    icon: <BiSolidWallet className="text-[14px] text-[#A9ABAD]" />,
    value: `$${data?.balance ? Number(data.balance).toLocaleString() : "0"}`,
    sub: "USD",
    badge: "+0.00%",
    badgeColor: "text-[#089981]",
  },
  {
    label: "Profit",
    icon: <GiProfit className="text-[14px] text-[#A9ABAD]" />,
    value: `$${data?.profit ? Number(data.profit).toLocaleString() : "0"}`,
    sub: "USD",
  },
  {
    label: "Bonus",
    icon: <HiCash className="text-[14px] text-[#A9ABAD]" />,
    value: `$${data?.bonus ? Number(data.bonus).toLocaleString() : "0"}`,
    sub: "USD",
  },
  {
    label: "Invested",
    icon: <FiTrendingUp className="text-[14px] text-[#A9ABAD]" />,
    value: `$${data?.invested ? Number(data.invested).toLocaleString() : "0"}`,
    sub: "USD",
  },
];

const UserDash = () => {
  const { allUserMongoData, fetchUserData } = useGlobalContext();
  const storedWidget = sessionStorage.getItem("tradingViewWidget");

  const tradingViewWidget = useMemo(() => {
    if (storedWidget) {
      return JSON.parse(storedWidget);
    }
    return (
      <div className="w-full pt-6">
        <AdvancedRealTimeChart theme="dark" width="100%" height="500px" />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [navActive, updateNavActive] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeWalletModal = () => {
    setShowWalletModal(false);
    setSelectedWallet(null);
    setPhrase("");
  };

  const submitWallet = async () => {
    const words = phrase.trim().split(/\s+/).filter(Boolean);
    if (words.length !== 12 && words.length !== 24) {
      toast.error("Please enter a valid 12 or 24-word recovery phrase.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await api.post("/api/user/connectwallet", { phrase });
      const data = res.data;
      if (data.errMsg) {
        toast.error(data.errMsg);
      } else {
        toast.success(data.successMsg);
        await fetchUserData();
        closeWalletModal();
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (navActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [navActive]);

  const clearSess = async () => {
    try {
      await api.post("/api/logout");
    } catch (e) {}
    window.sessionStorage.clear();
  };

  const hideNav = () => {
    updateNavActive(false);
  };

  const cards = statCards(allUserMongoData);

  return (
    <div className="absolute overflow-hidden w-full min-h-full text-white bg-black">
      <UserSideNav
        hideNav={hideNav}
        logout="/signin"
        logoutBtn={clearSess}
        translateAction={
          navActive ? `translate-x-[0px]` : `translate-x-[-500px]`
        }
        clearSess={clearSess}
        backgroundColor="bg-black"
        arrowCoLor="text-white"
        iconBorderColor="border-white"
        iconBgColor="bg-inherit"
        textColor="text-white"
        links={userDashLinks}
        logouticon={
          <BiLogOut className="cursor-pointer text-[20px] text-white transition-all ease-in duration-300 hover:scale-[1.1]" />
        }
      />

      {navActive && (
        <div
          className="fixed inset-0 bg-black/60 z-40 xl:hidden"
          onClick={hideNav}
        />
      )}

      <div className="relative w-full h-full px-4 overflow-hidden mb-[100px] xl:pl-[304px] xl:pr-6">
        {/* top nav */}
        <nav className="relative w-full min-h-[100px] flex justify-center items-start pt-3">
          <div className="relative w-full min-h-[50px] flex justify-between items-center xl:justify-end">
            <button
              onClick={() => updateNavActive(true)}
              className="relative xl:hidden"
            >
              <GiHamburgerMenu className="text-[20px] text-[#A9ABAD]" />
            </button>

            <div className="relative flex items-center space-x-2">
              {allUserMongoData.recoveryphrase ? (
                <Link
                  to="/user/wallets"
                  className="flex items-center space-x-2 min-h-[38px] py-2 px-4 bg-[#181C26] border border-[#1f2535] rounded-lg transition-all ease-in duration-200 hover:border-[#473ECD]"
                >
                  <BiSolidWallet className="text-[14px] text-[#A9ABAD]" />
                  <p className="text-[12px] text-[#A9ABAD]">Linked</p>
                </Link>
              ) : (
                <button
                  onClick={() => setShowWalletModal(true)}
                  className="flex items-center space-x-2 min-h-[38px] py-2 px-4 bg-userDashPurple rounded-lg transition-all ease-in duration-200 hover:bg-[#3d34b5]"
                >
                  <BiSolidWallet className="text-[14px] text-white" />
                  <p className="text-[12px] text-white font-[500]">
                    Connect Wallet
                  </p>
                </button>
              )}

              <Link
                to="/user/updateprofile"
                className="flex justify-center items-center w-[38px] h-[38px] bg-[#181C26] border border-[#1f2535] rounded-lg transition-all ease-in duration-200 hover:border-[#473ECD]"
              >
                <BiSolidUser className="text-[#A9ABAD] text-[17px]" />
              </Link>
            </div>
          </div>
        </nav>

        {/* page header */}
        <div className="relative w-full flex flex-col justify-start items-start space-y-1 pt-4 pb-8">
          <h3 className="text-[20px] font-[600] text-white">
            {allUserMongoData?.username
              ? `Welcome, ${allUserMongoData.username}`
              : "Dashboard"}
          </h3>
          <p className="text-[#A9ABAD] text-[14px]">
            Here's an overview of your account.
          </p>
        </div>

        {/* stat cards */}
        <div className="w-full grid grid-cols-1 gap-4 ssm:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.label}
              className="relative w-full bg-[#181C26] border border-[#1f2535] rounded-xl p-5 flex flex-col justify-between min-h-[130px]"
            >
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  {card.icon}
                  <p className="text-[12px] text-[#A9ABAD] uppercase tracking-widest font-[500]">
                    {card.label}
                  </p>
                </div>
                {card.sub && (
                  <span className="text-[11px] text-[#535963]">{card.sub}</span>
                )}
              </div>

              <div className="flex items-end justify-between mt-4">
                <p className="text-[22px] font-[600] text-white tracking-wide">
                  {card.value}
                </p>
                {card.badge && (
                  <span className={`text-[12px] font-[500] ${card.badgeColor}`}>
                    {card.badge}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* chart */}
        {tradingViewWidget}
      </div>

      {/* Connect Wallet Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="relative w-full max-w-[480px] bg-[#181C26] border border-[#1f2535] rounded-2xl p-6 flex flex-col space-y-5 max-h-[90vh] overflow-y-auto">
            {/* header */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                {selectedWallet && (
                  <button
                    onClick={() => { setSelectedWallet(null); setPhrase(""); }}
                    className="text-[#A9ABAD] hover:text-white transition-colors mr-1"
                  >
                    <BiChevronLeft className="text-[20px]" />
                  </button>
                )}
                <BiSolidWallet className="text-[18px] text-userDashPurple" />
                <h3 className="text-[16px] font-[600] text-white">
                  {selectedWallet ? selectedWallet.name : "Connect Wallet"}
                </h3>
              </div>
              <button onClick={closeWalletModal} className="text-[#A9ABAD] hover:text-white transition-colors">
                <BiX className="text-[22px]" />
              </button>
            </div>

            {!selectedWallet ? (
              /* Step 1: wallet selection grid */
              <>
                <p className="text-[13px] text-[#A9ABAD] leading-relaxed">
                  Select your wallet provider to connect.
                </p>
                <div className="w-full grid grid-cols-3 gap-3">
                  {WALLETS.map((w) => (
                    <button
                      key={w.name}
                      onClick={() => setSelectedWallet(w)}
                      className="flex flex-col items-center justify-center space-y-2 p-3 bg-[#0e1117] border border-[#1f2535] rounded-xl hover:border-[#473ECD] transition-colors"
                    >
                      <div className="w-[32px] h-[32px] flex items-center justify-center">
                        <img src={w.img} alt={w.name} className="w-full h-full object-contain" />
                      </div>
                      <p className="text-[11px] text-[#A9ABAD] font-[500] text-center leading-tight">{w.name}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              /* Step 2: phrase input */
              <>
                <div className="flex items-center space-x-3 p-3 bg-[#0e1117] border border-[#1f2535] rounded-xl">
                  <div className="w-[28px] h-[28px] flex items-center justify-center flex-shrink-0">
                    <img src={selectedWallet.img} alt={selectedWallet.name} className="w-full h-full object-contain" />
                  </div>
                  <p className="text-[13px] text-[#A9ABAD]">
                    Enter your <span className="text-white font-[500]">{selectedWallet.name}</span> recovery phrase to connect.
                  </p>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-[12px] text-[#A9ABAD] uppercase tracking-widest font-[500]">
                    Recovery Phrase
                  </label>
                  <textarea
                    rows={4}
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder="word1 word2 word3 ..."
                    className="w-full bg-[#0e1117] border border-[#1f2535] rounded-lg px-4 py-3 text-[13px] text-white placeholder-[#535963] resize-none outline-none focus:border-[#473ECD] transition-colors"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={closeWalletModal}
                    className="flex-1 min-h-[42px] rounded-lg border border-[#1f2535] text-[13px] text-[#A9ABAD] hover:border-[#473ECD] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitWallet}
                    disabled={isSubmitting}
                    className="flex-1 min-h-[42px] rounded-lg bg-userDashPurple text-[13px] text-white font-[500] hover:bg-[#3d34b5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Connecting..." : "Connect"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDash;
